import { Box, Button } from "@mui/material";
import { ReactNode } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslate } from "@refinedev/core";

import { Form } from "@shared/components";
import {
  AgreementsAutocomplete,
  LevelsAutocomplete,
  ProductsAutocomplete,
} from "@shared/components/AutocompleteFields";

import { FinancialFilterValues, FinancialTab } from "../types";

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function yearOptions() {
  const current = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, index) => current - 4 + index);
}

function Field({ children, narrow = false }: { children: ReactNode; narrow?: boolean }) {
  return (
    <Box
      sx={{
        width: { xs: "100%", sm: narrow ? 168 : 260 },
        "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%" },
      }}
    >
      {children}
    </Box>
  );
}

interface FinancialFiltersProps {
  tab: FinancialTab;
  methods: UseFormReturn<FinancialFilterValues>;
  onApply: (values: FinancialFilterValues) => void;
}

export function FinancialFilters({ tab, methods, onApply }: FinancialFiltersProps) {
  const translate = useTranslate();
  const isMedia = tab === "media";
  const isClientsByCategory = tab === "clients_by_category";
  const isClientPlans = tab === "client_plans";
  const isClientsByPlan = tab === "clients_by_plan";
  const isPlansByCategory = tab === "plans_by_category";
  const showClientFilters = tab !== "plans_by_category";

  return (
    <Form methods={methods} onSubmit={onApply}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end" }}>
        <Field>
          <AgreementsAutocomplete
            name="agreement_id"
            label={translate("financial_admin.filters.agreement")}
          />
        </Field>
        {!isClientsByCategory && !isMedia && !isClientPlans && !isClientsByPlan && (
          <Field>
            <ProductsAutocomplete
              name="product_id"
              label={translate("financial_admin.filters.product")}
            />
          </Field>
        )}
        <Field>
          <LevelsAutocomplete
            name="level_id"
            label={translate("financial_admin.filters.level")}
          />
        </Field>
        {!isClientsByCategory && !isMedia && !isPlansByCategory && !isClientPlans && (
          <Field>
            <Form.Input
              name="plan_slug_integration"
              label={translate("financial_admin.filters.plan_slug_integration")}
            />
          </Field>
        )}
        {showClientFilters && !isClientsByCategory && (
          <Field>
            <Form.Input
              name="cpf"
              label={translate("financial_admin.filters.cpf")}
              required={tab === "client_plans"}
            />
          </Field>
        )}
        {showClientFilters && !isClientsByCategory && !isMedia && !isClientPlans && !isClientsByPlan && (
          <Field>
            <Form.Input
              name="user_id"
              label={translate("financial_admin.filters.user_id")}
            />
          </Field>
        )}
        {showClientFilters && (
          <>
            <Field narrow>
              <Form.Select
                name="year"
                label={translate("financial_admin.filters.year")}
                options={[
                  { value: "", label: translate("financial_admin.filters.all") },
                  ...yearOptions().map((year) => ({ value: String(year), label: String(year) })),
                ]}
              />
            </Field>
            <Field narrow>
              <Form.Select
                name="month"
                label={translate("financial_admin.filters.month")}
                options={[
                  { value: "", label: translate("financial_admin.filters.all") },
                  ...MONTHS.map((month) => ({
                    value: String(month),
                    label: translate(`financial_admin.months.${month}`),
                  })),
                ]}
              />
            </Field>
          </>
        )}
        {isMedia && (
          <Field>
            <Form.Select
              name="media_type"
              label={translate("financial_admin.filters.media_type")}
              options={[
                { value: "", label: translate("financial_admin.filters.all") },
                { value: "book", label: translate("financial_admin.media_types.book") },
                { value: "audiobook", label: translate("financial_admin.media_types.audiobook") },
                { value: "magazine", label: translate("financial_admin.media_types.magazine") },
                { value: "magazine-partiu", label: translate("financial_admin.media_types.magazine-partiu") },
              ]}
            />
          </Field>
        )}
        <Button type="submit" variant="contained" sx={{ height: 45, minWidth: 128 }}>
          {translate("financial_admin.actions.apply")}
        </Button>
      </Box>
    </Form>
  );
}
